import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subject, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  map,
} from 'rxjs/operators';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { UserService } from 'src/app/services/core/user.service';

@Component({
    selector: 'app-note-sidebar',
    templateUrl: './note-sidebar.component.html',
    styleUrls: ['./note-sidebar.component.scss'],
    animations: [
        trigger('sidebarCollapse', [
            state('expanded', style({
                width: '256px',
                opacity: 1
            })),
            state('collapsed', style({
                width: '48px',
                opacity: 0.95
            })),
            transition('expanded <=> collapsed', [
                animate('300ms ease-in-out')
            ])
        ]),
        trigger('toggleButtonHover', [
            state('normal', style({
                transform: 'scale(1)',
                opacity: 0.9
            })),
            state('hovered', style({
                transform: 'scale(1.05)',
                opacity: 1
            })),
            transition('normal <=> hovered', [
                animate('150ms ease-in-out')
            ])
        ]),
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0, transform: 'scale(0.8)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
            ]),
            transition(':leave', [
                animate('150ms ease-in', style({ opacity: 0, transform: 'scale(0.8)' }))
            ])
        ])
    ],
    standalone: false
})
export class NoteSidebarComponent implements OnInit {
  private readonly noteModuleId = 23;

  @Input() noteForm!: UntypedFormGroup;
  @Input() noteId!: number;
  @Input() mode!: 'create' | 'edit' | 'view';
  @Input() createdOn?: string;
  @Input() createdBy?: string;
  @Input() moduleLabel?: string;
  @Input() projectName?: string;
  @Input() statuses: any[] = [];
  @Input() selectedStatus: any = null;
  @Input() modules: any[] = [];
  @Input() showModuleControls: boolean = true;

  @Output() fieldChange = new EventEmitter<{field: string, value: any}>();
  @Output() statusChange = new EventEmitter<any>();
  @Output() onCollapse = new EventEmitter<boolean>();
  @Output() moduleSelected = new EventEmitter<any>();
  @Output() referenceSelected = new EventEmitter<any>();
  @Output() selfAssignChange = new EventEmitter<boolean>();

  isCollapsed = false;
  isReadOnly = false;
  searchQuery = '';
  isToggleButtonHovered = false;

  // Reference search
  public referenceSearchInput$ = new Subject<string>();
  public referenceSearchResults$: Observable<any[]> = of([]);
  public referenceSearchLoading: boolean = false;

  constructor(
    private api: BackendApiService,
    private userService: UserService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeReferenceSearch();
  }

  private initializeReferenceSearch(): void {
    // Reference search observable
    this.referenceSearchResults$ = this.referenceSearchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchTerm: string): Observable<any[]> => {
        const moduleId = this.noteForm?.get('module_id')?.value;

        // Early return if no module selected or search term too short
        if (!moduleId || !searchTerm || searchTerm.trim().length < 2) {
          this.referenceSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }

        // Check if module is note module (23) - don't search
        if (Number(moduleId) === this.noteModuleId) {
          this.referenceSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }

        const selectedModule = this.modules?.find(
          (m: any) => Number(m.id) === Number(moduleId)
        );

        if (!selectedModule) {
          this.referenceSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }

        const payload = {
          type: selectedModule.slug,
          module_id: selectedModule.id,
          search: searchTerm.trim(),
        };

        // Set loading to true only when actually making API call
        this.referenceSearchLoading = true;
        this.cdr.markForCheck();

        return this.api
          .postApi('task-template/search-reference-numbers', payload)
          .pipe(
            map((res: any) => {
              this.referenceSearchLoading = false;
              this.cdr.markForCheck();
              const list = res?.data?.data || res?.data || [];
              return list.map((item: any) => ({
                id: item.id,
                name:
                  item.data ||
                  item.name ||
                  item.reference_number ||
                  item.number ||
                  item.id,
              }));
            }),
            catchError(() => {
              this.referenceSearchLoading = false;
              this.cdr.markForCheck();
              return of([]);
            })
          );
      })
    );
  }

  /**
   * Expand sidebar (from collapsed state)
   */
  public expandSidebar(): void {
    this.isCollapsed = false;
    this.onCollapse.emit(false);
  }

  /**
   * Collapse sidebar (to collapsed state)
   */
  public collapseSidebar(): void {
    this.isCollapsed = true;
    this.onCollapse.emit(true);
  }

  /**
   * Get animation state for sidebar collapse trigger
   */
  public getSidebarAnimationState(): string {
    return this.isCollapsed ? 'collapsed' : 'expanded';
  }

  /**
   * Get animation state for toggle button hover
   */
  public getToggleButtonAnimationState(): string {
    return this.isToggleButtonHovered ? 'hovered' : 'normal';
  }

  /**
   * Handle search input changes
   * @param query Search query string
   */
  public handleSearch(query: string): void {
    this.searchQuery = query;
    // Future: Emit search event to parent or implement filter logic
  }

  /**
   * Handle field value changes and emit to parent
   * @param field Field name
   * @param event Change event
   */
  public handleFieldChange(field: string, event: any): void {
    const value = event?.target?.value ?? event;
    this.fieldChange.emit({ field, value });
  }

  /**
   * Handle status change from mat-select dropdown
   * Emits full status object to parent (matches task sidebar pattern)
   * @param event MatSelectChange event
   */
  public handleStatusChange(event: any): void {
    const statusId = event.value;
    const selectedStatusObj = this.statuses.find((s) => s.id === statusId);

    if (selectedStatusObj) {
      this.statusChange.emit(selectedStatusObj);
    }
  }

  /**
   * Handle flag toggle change
   * @param event MatSlideToggleChange event
   */
  public onFlagToggle(event: any): void {
    const value = event?.checked ?? event;
    this.fieldChange.emit({ field: 'flag', value });
  }

  /**
   * Get selected status name for display in trigger
   * @returns Status name or empty string if not found
   */
  public getSelectedStatusName(): string {
    const statusId = this.noteForm?.get('status')?.value;
    if (!statusId || !this.statuses) return '';

    const status = this.statuses.find((s) => s.id === statusId);
    return status?.name || '';
  }

  /**
   * Get selected status color for display in trigger
   * @returns Status color (hex code) or default gray if not found
   */
  public getSelectedStatusColor(): string {
    const statusId = this.noteForm?.get('status')?.value;
    if (!statusId || !this.statuses) return '#9ca3af';

    const status = this.statuses.find((s) => s.id === statusId);
    return status?.color || '#9ca3af';
  }

  /**
   * Get selected status lock state for display in trigger
   * @returns True if selected status has lock_status enabled
   */
  public getSelectedStatusLocked(): boolean {
    const statusId = this.noteForm?.get('status')?.value;
    if (!statusId || !this.statuses) return false;

    const status = this.statuses.find((s) => s.id === statusId);
    return status?.lock_status || false;
  }

  /**
   * Handle module selection change from dropdown
   */
  public onModuleChange(moduleId: any): void {
    // Find the full module object by ID
    const selectedModuleObj = this.modules.find((m: any) => m.id == moduleId);

    if (selectedModuleObj) {
      this.moduleSelected.emit(selectedModuleObj);
      this.fieldChange.emit({
        field: 'module_id',
        value: selectedModuleObj.id,
      });
      this.cdr.markForCheck();
    }
  }

  /**
   * Handle reference selection from typeahead dropdown
   */
  public onReferenceSelect(reference: any): void {
    this.referenceSelected.emit(reference);
    this.fieldChange.emit({ field: 'reference_id', value: reference?.id });
  }

  /**
   * Handle self-assign toggle change
   */
  public onSelfAssignToggle(event: any): void {
    const isOn = !!(event && (event.checked ?? event.target?.checked));

    // Always emit self_assign value (1 or 0)
    this.fieldChange.emit({ field: 'self_assign', value: isOn ? 1 : 0 });
    this.selfAssignChange.emit(isOn);

    if (isOn) {
      // When ON: Set module_id to 11 (Staff module) and reference_id to logged-in user ID
      const loginUserId = this.userService?.getID?.();
      const referenceValue = loginUserId ? Number(loginUserId) : null;

      this.noteForm?.get('module_id')?.setValue(11);
      this.noteForm?.get('reference_id')?.setValue(referenceValue);

      this.fieldChange.emit({ field: 'module_id', value: 11 });
      this.fieldChange.emit({ field: 'reference_id', value: referenceValue });
    } else {
      // When OFF: Clear module_id and reference_id
      this.noteForm?.get('module_id')?.setValue(null);
      this.noteForm?.get('reference_id')?.setValue(null);
      this.fieldChange.emit({ field: 'module_id', value: null });
      this.fieldChange.emit({ field: 'reference_id', value: null });
    }
    this.cdr.markForCheck();
  }
}
